/**
 * I SIMBOLI CANONICI, e come si ritrovano sul server di un broker.
 *
 * ---------------------------------------------------------------------------
 * LA LISTA STA QUI, E IN NESSUN ALTRO POSTO
 * ---------------------------------------------------------------------------
 *
 * «Canonico» e' il nome con cui questo gestionale chiama uno strumento; il nome
 * sul server e' un'altra cosa e cambia da broker a broker — `XAUUSD`, `XAUUSD-E`,
 * `XAUUSD.r`. La mappatura fra i due e' `SymbolMapping`.
 *
 * La lista sta in `shared` perche' la leggono tre posti che non possono
 * importarsi a vicenda: la mappatura automatica alla connessione, il comando
 * che la rifa' sui server gia' esistenti, e la pagina Server. Una seconda copia
 * sarebbe una seconda idea di quali strumenti esistono, e la seconda resterebbe
 * indietro.
 *
 * ---------------------------------------------------------------------------
 * LA RICERCA DEL NOME: PRIMA L'ESATTO, POI IL SUFFISSO, E BASTA
 * ---------------------------------------------------------------------------
 *
 * Due passi, in quest'ordine:
 *
 *   1. **il nome esatto**. Se il server ha `XAUUSD`, quello e' il simbolo: non
 *      si guarda nemmeno se esistono `XAUUSD.m` o `XAUUSDm`. E' il passo che
 *      rende la ricerca decidibile sui broker che offrono anche i micro-lotti;
 *   2. **canonico + suffisso**, cioe' un nome che, tolti i separatori, comincia
 *      col canonico: `XAUUSD-E`, `XAUUSD.r`, `XAUUSDm`.
 *
 * **Nessun alias.** `GOLD` e' oro per una persona, non per questa funzione: un
 * dizionario di sinonimi sarebbe una tabella di ipotesi, e un'ipotesi sbagliata
 * qui manda a mercato lo strumento sbagliato. I simboli che non si ritrovano
 * per nome si mappano a mano, con una persona che sceglie (DECISIONE 27).
 *
 * **Piu' di un candidato non e' una scelta: e' un'ambiguita'**, e si dichiara
 * invece di essere risolta col primo in ordine alfabetico. Fra `XAUUSD.m` e
 * `XAUUSDm` la differenza e' il contract size — 10 contro 100 — cioe' un errore
 * di dieci volte su ogni importo, in silenzio.
 */
/**
 * Gli strumenti su cui questo gestionale opera.
 *
 * Non e' «tutto quello che il broker offre»: e' l'elenco di quello che una
 * coppia puo' usare, e un simbolo si potra' scegliere in un'operazione solo se
 * e' mappato su TUTT'E DUE i server della coppia.
 */
export const CANONICAL_SYMBOLS = [
    "EURUSD",
    "GBPUSD",
    "USDJPY",
    "XAUUSD",
    "USDCAD",
    "EURJPY",
    "GBPJPY",
    "USDCHF",
    "BTCUSD",
];
export function isCanonicalSymbol(name) {
    return CANONICAL_SYMBOLS.includes(name.trim().toUpperCase());
}
/** Il nome senza separatori e in maiuscolo: `XAUUSD-E` -> `XAUUSDE`. */
export const normalizeSymbolName = (name) => name.toUpperCase().replace(/[^A-Z0-9]/g, "");
/**
 * Due nomi si somigliano se, tolti i separatori, il candidato comincia col
 * canonico. `XAUUSD` trova `XAUUSD`, `XAUUSD-E`, `XAUUSD.m`, `XAUUSDm`; non
 * trova `XAGUSD` ne' `EURUSD`.
 *
 * La somiglianza **restringe**, non decide: quando i candidati sono piu' d'uno
 * sceglie una persona.
 */
export const looksLikeCanonical = (candidate, canonical) => normalizeSymbolName(candidate).startsWith(normalizeSymbolName(canonical));
/**
 * Il nome di un canonico sul server, fra quelli che il server dichiara.
 *
 * `names` e' la lista COMPLETA dei simboli del server (`symbolList`): il
 * filtro si fa qui, perche' e' qui che vive la regola.
 */
export function matchCanonical(canonical, names) {
    const target = canonical.trim().toUpperCase();
    // 1 — Il nome esatto vince su tutto, e si restituisce come lo scrive il
    //     server: e' quello che andra' nell'ordine.
    const esatto = names.find((n) => n.trim().toUpperCase() === target);
    if (esatto)
        return { kind: "EXACT", brokerSymbol: esatto };
    // 2 — Canonico + suffisso. Ordinati per rendere il messaggio ripetibile.
    const simili = names.filter((n) => looksLikeCanonical(n, target)).sort();
    if (simili.length === 1)
        return { kind: "SUFFIX", brokerSymbol: simili[0] };
    if (simili.length === 0)
        return { kind: "NOT_FOUND" };
    return { kind: "AMBIGUOUS", candidates: simili };
}
//# sourceMappingURL=canonical-symbols.js.map